// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useDrawer } from '@shared/ui-kit/hooks/use-drawer';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { SvgIconName } from '@shared/ui-kit/components/app-icon';
import {
  IlliquidAssetCurrency,
  IlliquidAssetType,
  type IlliquidAssetEvent,
  type PeVcSubcategory,
} from '@shared/data-access/types/engine-types';
import {
  illiquidAssetFormSchema,
  type IlliquidAssetFormData,
} from '../../types/illiquid-asset-form';
import {
  buildDefaultSubcategoryByAssetType,
  buildIlliquidAssetDefaultValues,
  buildSubcategoryOptionsByAssetType,
} from '../../utils/illiquid-asset-form-defaults';
import dayjs from 'dayjs';

export interface UseIlliquidAssetDrawerProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onOpen: () => void;
  readonly onSubmit: (
    data: IlliquidAssetFormData,
    draftEvents: IlliquidAssetEvent[],
  ) => void | Promise<void>;
  readonly isCreating?: boolean;
}

export const useIlliquidAssetDrawer = ({
  open,
  onClose,
  onOpen,
  onSubmit,
  isCreating = false,
}: UseIlliquidAssetDrawerProps) => {
  const { t } = useTranslation();

  const todayIso = useMemo(() => dayjs().format('YYYY-MM-DD'), []);

  const defaultValues = useMemo<IlliquidAssetFormData>(
    () => buildIlliquidAssetDefaultValues(todayIso),
    [todayIso],
  );

  const form = useForm<IlliquidAssetFormData>({
    resolver: zodResolver(illiquidAssetFormSchema),
    mode: 'onChange',
    defaultValues,
  });

  const [draftEvents, setDraftEvents] = useState<IlliquidAssetEvent[]>([]);

  const onDrawerClose = useCallback(() => {
    form.reset(defaultValues);
    setDraftEvents([]);

    onClose();
  }, [defaultValues, form, onClose]);

  const [DrawerRenderer, drawerControls] = useDrawer({
    open,
    onClose: onDrawerClose,
    onOpen,
  });

  const category = form.watch('category');

  const currency = form.watch('currency') ?? IlliquidAssetCurrency.EUR;

  const assetTypeTiles = useMemo(() => {
    return [
      {
        value: IlliquidAssetType.RealEstate,
        label: t('illiquid-asset-drawer.options.asset-type.real_estate', {
          ns: 'components',
        }),
        tooltip: t('illiquid-asset-drawer.options.asset-type.real_estate', {
          ns: 'components',
        }),
        icon: 'Building' as SvgIconName,
      },
      {
        value: IlliquidAssetType.Company,
        label: t('illiquid-asset-drawer.options.asset-type.company', {
          ns: 'components',
        }),
        tooltip: t('illiquid-asset-drawer.options.asset-type.company', {
          ns: 'components',
        }),
        icon: 'Briefcase' as SvgIconName,
      },
      {
        value: IlliquidAssetType.PeVc,
        label: t('illiquid-asset-drawer.options.asset-type.pe_vc', {
          ns: 'components',
        }),
        tooltip: t('illiquid-asset-drawer.options.asset-type.pe_vc_expanded', {
          ns: 'components',
        }),
        icon: 'TrendingUp' as SvgIconName,
      },
      {
        value: IlliquidAssetType.Tangible,
        label: t('illiquid-asset-drawer.options.asset-type.tangible', {
          ns: 'components',
        }),
        tooltip: t('illiquid-asset-drawer.options.asset-type.tangible', {
          ns: 'components',
        }),
        icon: 'Palette' as SvgIconName,
      },
      {
        value: IlliquidAssetType.OtherIlliquid,
        label: t('illiquid-asset-drawer.options.asset-type.other_illiquid', {
          ns: 'components',
        }),
        tooltip: t('illiquid-asset-drawer.options.asset-type.other_illiquid', {
          ns: 'components',
        }),
        icon: 'Boxes' as SvgIconName,
      },
    ] as const;
  }, [t]);

  const subcategoryOptionsByAssetType = useMemo(
    () => buildSubcategoryOptionsByAssetType(),
    [],
  );

  const defaultSubcategoryByAssetType = useMemo(
    () => buildDefaultSubcategoryByAssetType(),
    [],
  );

  const subcategoryOptions = useMemo(() => {
    const keys = subcategoryOptionsByAssetType.get(category) ?? [];

    return keys.map(key => ({
      value: key,
      label: t(
        `illiquid-asset-drawer.options.subcategory.${category}.${key}` as any,
        { ns: 'components' },
      ),
    }));
  }, [category, subcategoryOptionsByAssetType, t]);

  const currencyOptions = useMemo(() => Object.values(IlliquidAssetCurrency), []);

  const isPeVc = useMemo(() => category === IlliquidAssetType.PeVc, [category]);

  const onAssetTypeSelect = useCallback(
    (nextCategory: IlliquidAssetFormData['category']) => {
      form.clearErrors('fund_manager');

      const name = form.getValues('name');

      const description = form.getValues('description');

      const nextCurrency = form.getValues('currency') ?? IlliquidAssetCurrency.EUR;

      setDraftEvents([]);

      if (nextCategory === IlliquidAssetType.PeVc) {
        form.reset({
          category: IlliquidAssetType.PeVc,
          name,
          subcategory: defaultSubcategoryByAssetType.get(
            IlliquidAssetType.PeVc,
          )! as PeVcSubcategory,
          description: description ?? '',
          currency: nextCurrency,
          current_valuation: 0,
          valuation_date: todayIso,
          fund_manager: '',
          total_commitment: 0,
        });

        return;
      }

      form.reset({
        ...buildIlliquidAssetDefaultValues(todayIso),
        category: nextCategory,
        name,
        description: description ?? '',
        currency: nextCurrency,
        subcategory: defaultSubcategoryByAssetType.get(nextCategory)!,
      } as IlliquidAssetFormData);
    },
    [defaultSubcategoryByAssetType, form, todayIso],
  );

  const onFormSubmit = useCallback(
    (data: IlliquidAssetFormData) => onSubmit(data, draftEvents),
    [draftEvents, onSubmit],
  );

  return {
    DrawerRenderer,
    open: drawerControls.open,
    onClose: onDrawerClose,
    onOpen: drawerControls.onOpen,
    t,
    form,
    category,
    isPeVc,
    assetTypeTiles,
    subcategoryOptions,
    onAssetTypeSelect,
    currencyOptions,
    draftEvents,
    onDraftEventsChange: setDraftEvents,
    draftDefaultCurrency: currency,
    onSubmit: onFormSubmit,
    isCreating,
  } as const;
};
